"use client";

import { useReducedMotion } from "motion/react";
import Image from "next/image";
import { useEffect, useRef } from "react";
import { DEMO_MEDIA_HEIGHT, DEMO_MEDIA_WIDTH } from "../_logic/constants";
import type { ReleaseDemo } from "../_logic/types";

interface ReleaseDemoMediaProps {
  demo: ReleaseDemo;
}

/**
 * A real product capture in a soft frame, with its caption. Videos start after mount so the
 * server and client render the same markup: they loop silently with controls to pause them, or
 * stay paused on their poster when the visitor prefers reduced motion.
 *
 * @param demo - The capture to show.
 * @returns The framed capture.
 */
export function ReleaseDemoMedia({ demo }: ReleaseDemoMediaProps): JSX.Element {
  // Hooks
  const reduceMotion = useReducedMotion();
  const videoRef = useRef<HTMLVideoElement>(null);

  // Effects
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (reduceMotion) {
      video.pause();
      return;
    }
    video.play().catch(() => {});
  }, [reduceMotion]);

  // Render
  return (
    <figure className="flex flex-col gap-3">
      <div className="rounded-3xl bg-landing-secondary p-2 md:p-3">
        <div className="overflow-hidden rounded-2xl border border-landing-primary bg-white">
          {demo.kind === "video" ? (
            <video
              ref={videoRef}
              src={demo.src}
              poster={demo.poster}
              aria-label={demo.alt}
              width={DEMO_MEDIA_WIDTH}
              height={DEMO_MEDIA_HEIGHT}
              controls
              muted
              loop
              playsInline
              preload="metadata"
              className="block h-auto w-full"
            />
          ) : (
            <Image
              src={demo.src}
              alt={demo.alt}
              width={DEMO_MEDIA_WIDTH}
              height={DEMO_MEDIA_HEIGHT}
              sizes="(min-width: 1024px) 860px, 100vw"
              className="block h-auto w-full"
            />
          )}
        </div>
      </div>
      <figcaption className="text-body-sm text-landing-secondary">{demo.caption}</figcaption>
    </figure>
  );
}
